import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, MapPin } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    countryCode: '+91',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState({
    type: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullPhone = formData.phone
      ? `${formData.countryCode.trim()} ${formData.phone.trim()}`.trim()
      : '';
    const phoneDigits = fullPhone.replace(/\D/g, '');

    if (formData.phone && (phoneDigits.length < 7 || phoneDigits.length > 15)) {
      setSubmitState({
        type: 'error',
        message: 'Phone number must contain between 7 and 15 digits including country code.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitState({ type: '', message: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          company: formData.company,
          phone: fullPhone || null,
          message: formData.message || null
        })
      });

      let result = null;
      let responseText = '';

      if (!response.bodyUsed) {
        responseText = await response.text();
      }

      if (responseText) {
        try {
          result = JSON.parse(responseText);
        } catch {
          result = { message: responseText };
        }
      }

      if (!response.ok) {
        let errorMessage = result?.message || 'Unable to submit your request right now. Please try again.';

        if (Array.isArray(result?.detail)) {
          errorMessage = result.detail.map((item) => item.msg).join(' ');
        } else if (typeof result?.detail === 'string') {
          errorMessage = result.detail;
        } else if (typeof result?.message === 'string') {
          errorMessage = result.message;
        } else if (response.status === 422) {
          errorMessage = 'Please check your form details and try again.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error while submitting your request. Please try again shortly.';
        }

        throw new Error(errorMessage);
      }

      setSubmitState({
        type: 'success',
        message: result?.message || 'Thank you! Our team will reach out within 24 hours.'
      });
      setFormData({
        fullName: '',
        email: '',
        company: '',
        countryCode: '+91',
        phone: '',
        message: ''
      });
    } catch (error) {
      setSubmitState({
        type: 'error',
        message: error.message || 'Unable to submit your request right now. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Dark Navy */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center" data-testid="contact-header">
            <h1 className="text-h1 text-white mb-6">Get in Touch</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Transform Your Financial Operations: Join leading BFSI enterprises leveraging AI-native intelligence
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-h3 text-[#0A1628] mb-4">Reach the Cognifin Team</h2>
            <p className="text-body-lg text-[#6B7280] leading-relaxed">
              Share your requirements or request a demo and our team will get back to you.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#0A1628] mb-5">Contact Information</h2>
                
                <div className="space-y-6">
                  <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-[#1A56DB]/10 rounded-lg">
                          <Mail className="text-[#1A56DB]" size={24} />
                        </div>
                        <div>
                          <h3 className="text-[#0A1628] font-semibold mb-1">Email</h3>
                          <a href="mailto:info@cognifin.tech" className="text-[#1A56DB] hover:underline">
                            info@cognifin.tech
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-[#1A56DB]/10 rounded-lg">
                          <MapPin className="text-[#1A56DB]" size={24} />
                        </div>
                        <div>
                          <h3 className="text-[#0A1628] font-semibold mb-2">Office</h3>
                          <div className="text-[#6B7280] text-sm">
                            202, Durwankur, Plot no - 58,<br/>
                            Ashok Nagar, Pune 411007
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#0A1628] mb-4">What Happens Next</h3>
                  <div className="space-y-4">
                    {[
                      'We review your request within 24 hours.',
                      'We schedule a discovery or demo conversation.',
                      'We tailor the discussion to your workflow, team, and use case.'
                    ].map((step) => (
                      <div key={step} className="flex items-start gap-3">
                        <div className="mt-1 h-2.5 w-2.5 rounded-full bg-[#1A56DB] flex-shrink-0" />
                        <p className="text-sm leading-relaxed text-[#4B5563]">{step}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-5 text-xs leading-relaxed text-[#6B7280]">
                    Your details are used only to respond to your inquiry. No spam, no sharing.
                  </p>
                </CardContent>
              </Card>

            </div>

            {/* Contact Form */}
            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#0A1628] mb-2">Request a Demo</h2>
                <p className="text-[#6B7280] mb-6">Fill out the form below and our team will reach out within 24 hours</p>

                {submitState.message && (
                  <div
                    className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
                      submitState.type === 'success'
                        ? 'border-[#0E7490]/30 bg-[#0E7490]/10 text-[#0E7490]'
                        : 'border-[#B91C1C]/20 bg-[#FEF2F2] text-[#B91C1C]'
                    }`}
                    role="status"
                  >
                    {submitState.message}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label className="text-[#0A1628] text-sm font-medium mb-2 block">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="bg-white border-[#D1D5DB] text-[#374151] placeholder:text-[#6B7280] focus:border-[#1A56DB] focus:ring-[#1A56DB]"
                      placeholder="John Doe"
                      data-testid="full-name-input"
                    />
                  </div>

                  <div>
                    <label className="text-[#0A1628] text-sm font-medium mb-2 block">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-white border-[#D1D5DB] text-[#374151] placeholder:text-[#6B7280] focus:border-[#1A56DB] focus:ring-[#1A56DB]"
                      placeholder="john@company.com"
                      data-testid="email-input"
                    />
                  </div>

                  <div>
                    <label className="text-[#0A1628] text-sm font-medium mb-2 block">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="bg-white border-[#D1D5DB] text-[#374151] placeholder:text-[#6B7280] focus:border-[#1A56DB] focus:ring-[#1A56DB]"
                      placeholder="Your Company"
                      data-testid="company-input"
                    />
                  </div>

                  <div>
                    <label className="text-[#0A1628] text-sm font-medium mb-2 block">
                      Phone Number (optional)
                    </label>
                    <div className="grid grid-cols-[120px_1fr] gap-3">
                      <Input
                        type="text"
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        className="bg-white border-[#D1D5DB] text-[#374151] placeholder:text-[#6B7280] focus:border-[#1A56DB] focus:ring-[#1A56DB]"
                        placeholder="+1"
                        data-testid="country-code-input"
                      />
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-white border-[#D1D5DB] text-[#374151] placeholder:text-[#6B7280] focus:border-[#1A56DB] focus:ring-[#1A56DB]"
                        placeholder="98765 43210"
                        data-testid="phone-input"
                      />
                    </div>
                    <p className="mt-2 text-xs text-[#6B7280]">
                      Use international format with country code. Total digits should be between 7 and 15.
                    </p>
                  </div>

                  <div>
                    <label className="text-[#0A1628] text-sm font-medium mb-2 block">
                      Message
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="bg-white border-[#D1D5DB] text-[#374151] placeholder:text-[#6B7280] focus:border-[#1A56DB] focus:ring-[#1A56DB]"
                      placeholder="Tell us about your requirements..."
                      data-testid="message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold text-lg py-6 btn-hover-lift"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
